import { useCallback, useRef } from "react";
import { Button } from "../../../../components/Button/Button.tsx";
import { ContentContainer } from "../../../../components/Content/ContentContainer.tsx";
import { ThumbnailContent } from "../../../../components/Content/ThumbnailContent.tsx";
import { Dialog } from "../../../../components/Dialog/Dialog.tsx";
import { ClickableIcon } from "../../../../components/Icon/ClickableIcon.tsx";
import { Icon } from "../../../../components/Icon/Icon.tsx";
import { Spinner } from "../../../../components/Spinner/Spinner.tsx";
import { useAppMutation } from "../../../../http/useAppMutation.ts";
import { trackEvent } from "../../../../tracking/tracking.ts";
import { IMAGE_RESOURCE_TYPE } from "../../../Board/constants.ts";
import { FROM_INSPIRATION_PRESET } from "../../../boardState/constants.ts";
import type { BoardState } from "../../../boardState/types.ts";
import { GENERIC_STYLE_MODEL_UUID } from "../../../constants.ts";
import { useModels } from "../../../hooks/useModels.ts";
import { useRedirectToBoard } from "../../../hooks/useRedirectToBoard.ts";
import { useWorkspaceUuid } from "../../../hooks/useWorkspaceUuid.ts";
import type { InspirationContent } from "../../../types.ts";
import { useCreateBoard } from "../hooks/useCreateBoard.ts";

export const InspirationActionsDialog = ({
  inspirationContent,
  dialogOpen,
  onDialogChange,
}: {
  inspirationContent: InspirationContent;
  dialogOpen: boolean;
  onDialogChange: (dialogChange: boolean) => void;
}) => (
  <Dialog
    title="Start from this image"
    isOpen={dialogOpen}
    onOpenChange={(dialogChange: boolean) => {
      onDialogChange(dialogChange);
    }}
    content={
      <InspirationActionsContent inspirationContent={inspirationContent} />
    }
  />
);

const InspirationActionsContent = ({
  inspirationContent,
}: {
  inspirationContent: InspirationContent;
}) => {
  const redirectToBoard = useRedirectToBoard();
  const workspaceUuid = useWorkspaceUuid();
  const baseBoardState = useRef<Partial<BoardState>>({
    prompt: undefined,
    modelUuids: undefined,
    defaultGenerationRatio: undefined,
    selectedAssetUuid: undefined,
  });
  const boardUuid = useRef<string | null>(null);

  const openBoardWithPreset = useCallback(() => {
    if (!boardUuid.current) return;
    redirectToBoard({
      uuid: boardUuid.current,
      boardStatePreset: FROM_INSPIRATION_PRESET,
      baseBoardState: {
        prompt: baseBoardState.current.prompt,
        modelUuids: baseBoardState.current.modelUuids,
        defaultGenerationRatio: baseBoardState.current.defaultGenerationRatio,
        selectedAssetUuid: baseBoardState.current.selectedAssetUuid,
      },
    });
  }, [redirectToBoard]);

  const { mutate: createAsset, isLoading: isCreateAssetLoading } =
    useAppMutation({
      path: "assets/create",
      invalidate: [`workspaces/${workspaceUuid}/boards`],
      onSuccess: (response: { data: { uuid: string } }) => {
        baseBoardState.current.selectedAssetUuid = response.data.uuid;
        openBoardWithPreset();
      },
    }).mutation;

  const {
    createBoard: createBoardAndCreateAsset,
    isCreateBoardLoading: isCreateBoardAndCreateAssetLoading,
  } = useCreateBoard({
    onSuccess: (uuid) => {
      boardUuid.current = uuid;
      createAsset({
        board_uuid: boardUuid.current,
        content_uuid: inspirationContent.uuid,
      });
    },
  });

  const { createBoard, isCreateBoardLoading } = useCreateBoard({
    onSuccess: (uuid) => {
      boardUuid.current = uuid;
      openBoardWithPreset();
    },
  });

  const { models, isLoading: isModelsLoading } = useModels();

  const filteredModels = models.filter(
    (it) =>
      inspirationContent.reference_uuids.includes(it.uuid) &&
      it.uuid !== GENERIC_STYLE_MODEL_UUID,
  );

  return isModelsLoading ? (
    <div className="flex-row-center flex-fill">
      <Spinner size="sm" />
    </div>
  ) : (
    <div className="flex-row px-400 pb-200 gap-200 h-[600px]">
      <div className="flex-col-center flex-fill w-[720px] p-200 rounded-200 bg-primary">
        <ContentContainer
          resourceType={inspirationContent.resource_type}
          src={inspirationContent.url}
          width={inspirationContent.width ?? undefined}
          height={inspirationContent.height ?? undefined}
          contentClassName="rounded-300"
        />
      </div>
      <div className="flex-col p-300 gap-300 w-[250px]">
        <div className="flex-col gap-150">
          <Button
            size="md"
            iconName="RefreshCw"
            loading={isCreateBoardLoading}
            onClick={() => {
              trackEvent("inspirations:reuse_prompt_and_style", {
                inspiration_uuid: inspirationContent.uuid,
              });
              baseBoardState.current = {
                prompt: inspirationContent.prompt,
                modelUuids: inspirationContent.reference_uuids,
                defaultGenerationRatio:
                  inspirationContent.generation_ratio ?? "r_1_1",
              };
              createBoard();
            }}
          >
            Reuse prompt and style
          </Button>
          <Button
            size="md"
            iconName="SquarePen"
            variant="secondary"
            loading={isCreateAssetLoading || isCreateBoardAndCreateAssetLoading}
            onClick={() => {
              trackEvent("inspirations:edit_image", {
                inspiration_uuid: inspirationContent.uuid,
              });
              createBoardAndCreateAsset();
            }}
          >
            Edit image
          </Button>
        </div>
        <div className="h-025 border-t w-full" />
        <div className="flex-col gap-200 px-100 label-md-semibold text-nowrap">
          <div className="text-primary">Infos</div>
          <div
            className="grid gap-x-200 gap-y-150"
            style={{ gridTemplateColumns: "min-content 1fr" }}
          >
            <div className="text-disabled">Created</div>
            <div className="text-secondary">
              {new Date(inspirationContent.created_at).getDate()}/
              {new Date(inspirationContent.created_at).getMonth() + 1}/
              {new Date(inspirationContent.created_at).getFullYear()}
            </div>
            {filteredModels.length > 0 ? (
              <>
                {/* FIXME: The different model items should be buttons redirecting to the model detailed page */}
                <div className="text-disabled">Model used</div>
                <div className="flex-col gap-100 text-secondary">
                  {filteredModels.map((model) => (
                    <div key={model.uuid} className="flex-row w-full gap-100">
                      <div className="w-400 h-400 rounded-100 overflow-hidden">
                        {model.thumbnail_url ? (
                          <ThumbnailContent
                            resourceType={IMAGE_RESOURCE_TYPE}
                            src={model.thumbnail_url}
                            className="w-full h-full"
                          />
                        ) : (
                          <div className="w-full h-full flex-row-center bg-surface-emphasis-primary-rest">
                            <Icon
                              size="sm"
                              name="PimentoStar"
                              className="fill-button-primary-rest"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex-fill truncate">
                        {model.name ?? "Untitled"}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {inspirationContent.width && inspirationContent.height ? (
              <>
                <div className="text-disabled">Resolution</div>
                <div className="text-secondary">
                  {inspirationContent.width} x {inspirationContent.height}
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="h-025 border-t w-full" />
        <div className="flex-col gap-200 px-100 overflow-y-auto flex-fill">
          <div className="flex-row items-center gap-100 w-full">
            <div className="text-primary label-md-semibold flex-fill">
              Prompt
            </div>
            <ClickableIcon
              name="Copy"
              size="sm"
              variant="tertiary"
              tooltip={{
                content: "Copy prompt to clipboard",
                align: "start",
                alignOffset: -150,
              }}
              onClick={() =>
                navigator.clipboard.writeText(inspirationContent.prompt)
              }
            />
          </div>
          <div className="text-secondary label-md-default text-wrap">
            {inspirationContent.prompt}
          </div>
        </div>
      </div>
    </div>
  );
};
